'use client';
import { logger } from '@magicschool/logger';
import { registerMonkeyFetch } from '@magicschool/monkey-fetch';
import { toast } from '@magicschool/spellbook';
import { PageLoader } from 'components/PageLoader';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { StoreApi } from 'zustand';
import { hasRecoverableSession } from '@/features/auth/hasRecoverableSession';
import { useDeepCompareMemoize } from '@/hooks/useDeepCompareMemoize';
import { clientSupabase } from '@/util/supabase';
import { getForbiddenToastMessageId } from './forbiddenResponseToastMessageId';
import { createInitialState, type FullState, type InitialStateProps } from './store';
import { createStrictStore } from './zustand';

export const ZustandContext = createContext<StoreApi<FullState> | null>(null);

interface ZustandProviderProps {
  initialData?: InitialStateProps;
  children: ReactNode;
}
export const ZustandProvider = ({ children, initialData = {} }: ZustandProviderProps) => {
  const router = useRouter();
  const intl = useIntl();
  const [store, setStore] = useState<StoreApi<FullState> | null>(null);

  const stableInitialData = useDeepCompareMemoize(initialData);

  useEffect(() => {
    setStore(createStrictStore(createInitialState(stableInitialData)));

    logger.debug('Registering monkey fetch...');
    const { unregister } = registerMonkeyFetch(router, [
      {
        matcher: [/^\/api\/.*/, new RegExp(`^${window.location.origin}/api/.*`)],
        requestInterceptor: (url, options) => {
          const headers = new Headers({
            ...options.headers,
          });
          if (options.body && !headers.get('Content-Type') && !(options.body instanceof FormData)) {
            headers.set('Content-Type', 'application/json');
          }
          return {
            url,
            options: {
              ...options,
              credentials: 'include',
              mode: 'cors',
              headers,
            },
          };
        },
        responseErrorHandlers: {
          conflict: () => {
            toast({ title: intl.formatMessage({ id: 'toast.conflict' }), status: 'error' });
          },
          badRequest: () => {
            toast({ title: intl.formatMessage({ id: 'toast.bad-request' }), status: 'error' });
          },
          unauthorized: async ({ isRetry, response }) => {
            // Retry only on the original 401 — a non-session 401 still looks "recoverable" on retry, so isRetry always falls through to the toast.
            if (!isRetry) {
              const recoverable = await hasRecoverableSession(clientSupabase);
              if (recoverable) {
                logger.warn('Retrying after 401: a valid session exists, likely a cross-tab session race', {
                  url: response.url,
                  status: response.status,
                });
                return { retry: true };
              }
            }

            toast({ title: intl.formatMessage({ id: 'toast.no-access' }), status: 'error' });
            logger.warn("I'm sorry, Dave. I'm afraid I can't do that.");
            router.push('/');
          },
          forbidden: async ({ response }) => {
            const body = await response.json().catch(() => null);
            const messageId = getForbiddenToastMessageId(body, (id) => id in intl.messages);
            toast({ title: intl.formatMessage({ id: messageId }), status: 'error' });
          },
          aborted: () => {
            logger.debug('Request aborted');
          },
          unknown: ({ response }) => {
            logger.error('Unknown error', response?.body, response);
            toast({ title: intl.formatMessage({ id: 'toast.unknown-error' }), status: 'error' });
          },
        },
      },
    ]);

    return () => {
      logger.debug('Unregistering monkey fetch...');
      unregister();
    };
  }, [intl, router, stableInitialData]);

  if (!store) {
    return <PageLoader />;
  }

  return <ZustandContext.Provider value={store}>{children}</ZustandContext.Provider>;
};
